import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createGesture, IonContent, IonFooter, IonPage } from '@ionic/react';
import { Link } from 'react-router-dom';
import Nav from './Nav';
import { sidnavStore, SidenavState } from '../api/SidenavStore';
import { ApiService } from '../api/ApiService';
import { UserState, userStore } from '../api/AuthService';
import LoadFirstIn from '../components/walkthrough/LoadFirstIn';
import HeroChrome from '../components/HeroChrome';
import { FaqSvg, LoadHistorySvg, LoadSvg, PreplanSvg } from '../components/svgIcons';
import { UserUtils } from '../utils/UserUtils';
import { DateUtils } from '../utils/DateUtils';
import { config } from '../Constants';
import { runStatus } from '../components/walkthrough/FirstSignIn';
import { useLogPageVisit } from '../utils/logPageVisit';

function shortDate(date?: string): string {
  if (!date) return '—';
  const formatted = DateUtils.formatDate(date);
  if (!formatted) return '—';
  const parts = formatted.split('/');
  if (parts.length >= 2) return `${parts[0]}/${parts[1]}`;
  return formatted;
}

function formatPickupEta(deliveryEndTime?: string): string {
  if (!deliveryEndTime) return '—';
  const raw = String(deliveryEndTime).trim();
  if (!raw) return '—';
  const d = new Date(raw.includes('T') || raw.includes('-') ? raw : raw.replace(' ', 'T'));
  if (isNaN(d.getTime())) {
    return raw.replace(/,?\s*\d{4}\b/, '').replace(/\b\d{4}\b/, '').trim() || '—';
  }
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  let hours = d.getHours();
  const minutes = d.getMinutes();
  const ampm = hours >= 12 ? ' pm' : ' am';
  hours = hours % 12;
  if (hours === 0) hours = 12;
  const minStr = minutes === 0 ? '' : `:${minutes.toString().padStart(2, '0')}`;
  return `${months[d.getMonth()]} ${d.getDate()}, ${hours}${minStr}${ampm}`;
}

function useLoadData() {
  useLogPageVisit('LP Load', 'User viewed the Load page');
  const { loadInfo } = userStore((state: UserState) => ({
    loadInfo: state.loadInfo,
  }));

  const [mounted, setMounted] = useState(false);
  const [msgCount, setMsgCount] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);
  const [hasPrePlanned, setHasPrePlanned] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      setTimeout(() => setMounted(true), 30);
    });
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    ApiService.getPreplannedLoads('load')
      .then(it => setHasPrePlanned(!!it?.length))
      .catch(() => setHasPrePlanned(false));

    ApiService.chatConversationGet('load', {})
      .then((it: any) => {
        if (!Array.isArray(it)) return;
        let count = 0;
        for (let i = 0; i < it.length; i++) {
          if (it[i]?.lastMessage?.read !== 1) count += 1;
        }
        setMsgCount(count);
      })
      .catch(() => {});

    if (!UserUtils.isAdmin() && !UserUtils.isDriverManager() && !UserUtils.isInspector()) {
      ApiService.getPushNotifications('load')
        .then((it: any) => {
          if (!Array.isArray(it)) return;
          setNotificationCount(it.filter((notification: any) => !notification.read).length);
        })
        .catch(() => {});
    }
  }, []);

  const hasLoad = !!loadInfo;
  const origin = hasLoad
    ? [loadInfo!.shipperCity, loadInfo!.shipperState].filter(Boolean).join(', ') || '—'
    : '—';
  const destination = hasLoad
    ? [loadInfo!.consigneeCity, loadInfo!.consigneeState].filter(Boolean).join(', ') || '—'
    : 'No active load';
  const loadNumber = hasLoad
    ? String(loadInfo!.orderNumber || loadInfo!.loadNumber || '—')
    : '—';
  const equipment = hasLoad && loadInfo!.trailer ? String(loadInfo!.trailer) : '—';
  const pickupDate = hasLoad ? shortDate(loadInfo!.pickupStartTime) : '—';
  const arrivalDate = hasLoad ? shortDate(loadInfo!.deliveryStartTime) : '—';
  const eta = hasLoad ? formatPickupEta(loadInfo!.deliveryEndTime) : '—';
  const miles =
    hasLoad && loadInfo!.milesTotal != null ? Number(loadInfo!.milesTotal).toLocaleString() : '—';
  const milesToGo =
    hasLoad && loadInfo!.milesToDestination != null && !Number.isNaN(Number(loadInfo!.milesToDestination))
      ? Math.round(Number(loadInfo!.milesToDestination)).toLocaleString()
      : '—';

  let routeProgress: number | null = null;
  if (hasLoad && loadInfo!.milesTotal != null && loadInfo!.milesToDestination != null) {
    const total = Number(loadInfo!.milesTotal);
    const remaining = Number(loadInfo!.milesToDestination);
    if (!Number.isNaN(total) && total > 0 && !Number.isNaN(remaining)) {
      routeProgress = Math.min(1, Math.max(0, 1 - remaining / total));
    }
  }

  return {
    mounted,
    msgCount,
    notificationCount,
    hasLoad,
    hasPrePlanned,
    origin,
    destination,
    loadNumber,
    equipment,
    pickupDate,
    arrivalDate,
    eta,
    miles,
    milesToGo,
    routeProgress,
    statusLabel: hasLoad ? 'In Transit' : 'No Load',
  };
}

type LoadHeroSectionProps = {
  hasLoad: boolean;
  statusLabel: string;
  loadNumber: string;
  equipment: string;
  origin: string;
  destination: string;
  pickupDate: string;
  arrivalDate: string;
  routeProgress: number | null;
  msgCount: number;
  notificationCount: number;
  onMenuClick: () => void;
};

const LoadHeroSection: React.FC<LoadHeroSectionProps> = ({
  hasLoad,
  statusLabel,
  loadNumber,
  equipment,
  origin,
  destination,
  pickupDate,
  arrivalDate,
  routeProgress,
  msgCount,
  notificationCount,
  onMenuClick,
}) => {
  const pathRef = useRef<SVGPathElement>(null);
  const [marker, setMarker] = useState<{ x: number; y: number } | null>(null);
  const [animatedProgress, setAnimatedProgress] = useState(0);

  useEffect(() => {
    if (routeProgress == null) {
      setAnimatedProgress(0);
      return;
    }
    const from = 0;
    const to = Math.min(1, Math.max(0, routeProgress));
    const duration = 1400;
    const start = performance.now();
    let raf = 0;

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setAnimatedProgress(from + (to - from) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [routeProgress]);

  useLayoutEffect(() => {
    if (routeProgress == null || !pathRef.current) {
      setMarker(null);
      return;
    }
    const path = pathRef.current;
    const length = path.getTotalLength();
    if (!length) {
      setMarker(null);
      return;
    }
    const t = Math.min(0.97, Math.max(0.03, animatedProgress));
    const point = path.getPointAtLength(length * t);
    setMarker({ x: point.x, y: point.y });
  }, [routeProgress, animatedProgress]);

  return (
    <div className="home-hero">
      <HeroChrome
        onMenuClick={onMenuClick}
        msgCount={msgCount}
        notificationCount={notificationCount}
      />

      <div className="load-hero" id="loadHero">
        <div className="load-hero__status-row">
          <div className="load-hero__status-pill">
            <div
              className={`load-hero__status-dot${hasLoad ? ' load-hero__status-dot--pulse' : ''}`}
            />
            <div className="load-hero__status-label">{statusLabel}</div>
          </div>
        </div>

        <div className="load-hero__route" aria-hidden="true">
          <svg className="load-hero__route-svg" viewBox="0 0 320 118" fill="none">
            <g opacity="0.35">
              <path d="M28 20c22 30-4 46 10 74" stroke="currentColor" strokeWidth="1" />
              <path d="M120 6c-14 26 18 40 6 70" stroke="currentColor" strokeWidth="1" />
              <path d="M210 14c20 20-2 44 16 62" stroke="currentColor" strokeWidth="1" />
              <path d="M270 4c16 18 6 40 22 58" stroke="currentColor" strokeWidth="1" />
            </g>
            <path
              ref={pathRef}
              className="load-hero__route-path"
              d="M26 84 C 90 100, 150 96, 178 60 S 268 22, 292 30"
              stroke="var(--home-accent, #34AAFF)"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
            <circle
              cx="26"
              cy="84"
              r="5"
              fill="rgba(255,255,255,0.3)"
              stroke="currentColor"
              strokeWidth="1.5"
            />
            <circle cx="292" cy="30" r="6" fill="var(--home-accent, #34AAFF)" />
            {hasLoad ? (
              <circle
                className="load-hero__dest-pulse"
                cx="292"
                cy="30"
                r="10"
                fill="none"
                stroke="var(--home-accent, #34AAFF)"
                strokeWidth="1.5"
              />
            ) : null}
            {marker ? (
              <g className="load-hero__progress">
                <circle
                  className="load-hero__progress-ring"
                  cx={marker.x}
                  cy={marker.y}
                  r="14"
                  fill="none"
                  stroke="var(--home-accent, #34AAFF)"
                  strokeWidth="1.5"
                />
                <circle
                  className="load-hero__progress-halo"
                  cx={marker.x}
                  cy={marker.y}
                  r="11"
                  fill="none"
                  stroke="var(--home-accent, #34AAFF)"
                  strokeWidth="1.5"
                />
                <circle
                  className="load-hero__progress-core"
                  cx={marker.x}
                  cy={marker.y}
                  r="6.5"
                  fill="#ffffff"
                  stroke="var(--home-accent, #34AAFF)"
                  strokeWidth="2"
                />
              </g>
            ) : null}
          </svg>
        </div>

        <div className="load-hero__meta">
          {hasLoad ? `#${loadNumber} · ${equipment}` : 'No assigned trailer'}
        </div>
        {hasLoad ? <div className="load-hero__origin">{origin} → </div> : null}
        <div className="load-hero__destination home-display">{destination}</div>
        {hasLoad ? (
          <div className="load-hero__dates">
            <span>Picked up {pickupDate}</span>
            <span>Arriving {arrivalDate}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const Load = () => {
  const { close }: { close: () => void } = sidnavStore((state: SidenavState) => ({
    open: state.open,
    close: state.close,
  }));
  const { toggleSidenav }: { toggleSidenav: () => void } = sidnavStore((state: SidenavState) => ({
    toggleSidenav: state.toggle,
  }));

  const ref = React.useRef<HTMLDivElement>(null);
  const loadData = useLoadData();

  useEffect(() => {
    if (config.showSidenav) gestureInit();
  }, []);

  const gestureInit = () => {
    const card = ref.current;
    if (!card) return;
    const gesture = createGesture({
      el: card,
      gestureName: 'swipeMenu',
      onMove: () => {},
      onEnd: () => {
        runStatus ? console.log('locked') : toggleSidenav();
      },
    });
    gesture.enable();
  };

  return (
    <IonPage>
      <IonContent fullscreen className="design-a-content">
        <LoadFirstIn>
          <div
            ref={ref}
            className={`design-a${loadData.mounted ? ' design-a--mounted' : ''}`}
            onClick={() => close()}
            id="loadPage"
          >
            <LoadHeroSection
              hasLoad={loadData.hasLoad}
              statusLabel={loadData.statusLabel}
              loadNumber={loadData.loadNumber}
              equipment={loadData.equipment}
              origin={loadData.origin}
              destination={loadData.destination}
              pickupDate={loadData.pickupDate}
              arrivalDate={loadData.arrivalDate}
              routeProgress={loadData.routeProgress}
              msgCount={loadData.msgCount}
              notificationCount={loadData.notificationCount}
              onMenuClick={() => toggleSidenav()}
            />
            <div className="home-sheet">
              <div className="load-distance">
                <div className="load-distance__label">Distance</div>
                <div className="load-distance__grid">
                  <div className="load-distance__row">
                    <div className="load-distance__col load-distance__col--first">
                      <div className="load-distance__value">{loadData.miles}</div>
                      <div className="load-distance__unit">miles</div>
                    </div>
                    <div className="load-distance__col">
                      <div className="load-distance__value">{loadData.milesToGo}</div>
                      <div className="load-distance__unit">miles to go</div>
                    </div>
                  </div>
                  <div className="load-distance__col load-distance__col--eta">
                    <div className="load-distance__value">{loadData.eta}</div>
                    <div className="load-distance__unit">PTA</div>
                  </div>
                </div>
              </div>

              <div className="load-options-label">Load Options</div>
              <div className="load-options-grid">
                <Link to="/currentload" className="load-option" id="currentLoad">
                  <div className="load-option__icon">
                    <LoadSvg />
                  </div>
                  <div>
                    <div className="load-option__title">Current Load</div>
                    <div className="load-option__sub">Active delivery details</div>
                  </div>
                </Link>

                <Link to="/loadhistory" className="load-option" id="loadHistory">
                  <div className="load-option__icon">
                    <LoadHistorySvg />
                  </div>
                  <div>
                    <div className="load-option__title">Load History</div>
                    <div className="load-option__sub">Past deliveries &amp; records</div>
                  </div>
                </Link>

                {loadData.hasPrePlanned ? (
                  <Link to="/preplanned" className="load-option load-option--wide" id="prePlanned">
                    <div className="load-option__icon">
                      <PreplanSvg />
                    </div>
                    <div>
                      <div className="load-option__title">Preplanned</div>
                      <div className="load-option__sub">Upcoming assigned loads</div>
                    </div>
                  </Link>
                ) : (
                  <Link to="/loadselection" className="load-option load-option--wide" id="prePlanned">
                    <div className="load-option__icon">
                      <FaqSvg />
                    </div>
                    <div>
                      <div className="load-option__title">Load Selection</div>
                      <div className="load-option__sub">Available loads you can choose from</div>
                    </div>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </LoadFirstIn>
      </IonContent>
      <IonFooter>
        <Nav pageName="Load" />
      </IonFooter>
    </IonPage>
  );
};

export default Load;
